
import { RealTimeAttendanceChart, ComparisonChart, InTimePieChart, RegularContractualComparisonChart } from '../components/AttendanceChart';
import { useNavigate } from 'react-router-dom';
import { attendanceApi } from '../services/api';
import empIcon from '../../../assets/Layer_2.png';
import presentIcon from '../../../assets/Group 307.png';
import absentIcon from '../../../assets/Group 308.png';
import prevIcon from '../../../assets/Group 309.png';
import { useEffect, useMemo } from 'react';
import { useAttendanceCache } from '../context/AttendanceContext';
import { BarChart3, Building2, CalendarDays, ChevronRight, TrendingUp } from 'lucide-react';

// Types for stat data
type BreakdownItem = { label: string; value: string };
type StatCard = { label: string; value: string; breakdown: BreakdownItem[]; icon: string; color?: string; iconColor?: string };

const initialStatData: StatCard[] = [
  { label: 'TOTAL EMPLOYEE', value: '--', breakdown: [{ label: 'Regular', value: '--' }, { label: 'Contractual', value: '--' }], icon: empIcon, color: 'bg-[#fff]', iconColor: '#57C3F2' },
  { label: 'PRESENT TODAY', value: '--', breakdown: [{ label: 'Regular', value: '--' }, { label: 'Contractual', value: '--' }], icon: presentIcon, color: 'bg-[#fff]', iconColor: '#279E15' },
  { label: 'ABSENT TODAY', value: '--', breakdown: [{ label: 'Regular', value: '--' }, { label: 'Contractual', value: '--' }], icon: absentIcon, color: 'bg-[#FFF]', iconColor: '#FF0000' },
  { label: 'PREVIOUS ATTENDANCE', value: '--', breakdown: [{ label: 'Regular', value: '--' }, { label: 'Contractual', value: '--' }], icon: prevIcon, color: 'bg-[#fff]', iconColor: '#E4B124' },
];

interface AttendanceDashboardProps {
  role: string;
}

// TODO: Replace with API data based on role
// You can use useEffect here to fetch data for the given role
export default function AttendanceDashboard({ role }: AttendanceDashboardProps) {
  const navigate = useNavigate();
  const { countsData, setCachedCounts } = useAttendanceCache();

  // Compute stat cards using useMemo - updates only when cache data changes
  const statData = useMemo(() => {
    const result = [...initialStatData];

    if (countsData.totalEmployee) {
      result[0] = {
        ...result[0],
        value: countsData.totalEmployee.totalEmployee.toLocaleString(),
        breakdown: [
          { label: 'Regular', value: countsData.totalEmployee.totalRegisteredEmp.toLocaleString() },
          { label: 'Contractual', value: countsData.totalEmployee.totalContractEmpCount.toLocaleString() }
        ]
      };
    }

    if (countsData.presentToday) {
      result[1] = {
        ...result[1],
        value: countsData.presentToday.totalPresentToday.toLocaleString(),
        breakdown: [
          { label: 'Regular', value: countsData.presentToday.totalPresentRegToday.toLocaleString() },
          { label: 'Contractual', value: countsData.presentToday.totalContractualPresentToday.toLocaleString() }
        ]
      };
    }

    if (countsData.absentToday) {
      result[2] = {
        ...result[2],
        value: countsData.absentToday.totalAbsentToday.toLocaleString(),
        breakdown: [
          { label: 'Regular', value: countsData.absentToday.totalAbsentRegToday.toLocaleString() },
          { label: 'Contractual', value: countsData.absentToday.totalContractualAbsentToday.toLocaleString() }
        ]
      };
    }

    if (countsData.previousDay) {
      result[3] = {
        ...result[3],
        value: countsData.previousDay.totalPreviousDayAttendance.toLocaleString(),
        breakdown: [
          { label: 'Regular', value: countsData.previousDay.totalPreviousDayAttendanceReg.toLocaleString() },
          { label: 'Contractual', value: countsData.previousDay.totalPreviousDayContractualAttendance.toLocaleString() }
        ]
      };
    }

    return result;
  }, [countsData]);

  useEffect(() => {
    if (!countsData.totalEmployee) {
      attendanceApi.getTotalEmployees().then((data) => {
        if (data !== null && typeof data.totalEmployee === 'number' && typeof data.totalRegisteredEmp === 'number' && typeof data.totalContractEmpCount === 'number') {
          setCachedCounts('totalEmployee', data);
        }
      });
    }
  }, [countsData.totalEmployee, setCachedCounts]);

  useEffect(() => {
    if (!countsData.presentToday) {
      attendanceApi.getStaffPresentToday().then((data) => {
        if (data !== null && typeof data.totalPresentToday === 'number' && typeof data.totalPresentRegToday === 'number' && typeof data.totalContractualPresentToday === 'number') {
          setCachedCounts('presentToday', data);
        }
      });
    }
  }, [countsData.presentToday, setCachedCounts]);

  useEffect(() => {
    if (!countsData.absentToday) {
      attendanceApi.getStaffAbsentToday().then((data) => {
        if (data !== null && typeof data.totalAbsentToday === 'number' && typeof data.totalAbsentRegToday === 'number' && typeof data.totalContractualAbsentToday === 'number') {
          setCachedCounts('absentToday', data);
        }
      });
    }
  }, [countsData.absentToday, setCachedCounts]);

  useEffect(() => {
    if (!countsData.previousDay) {
      attendanceApi.getTotalPreviousDayAttendance().then((data) => {
        if (data !== null && typeof data.totalPreviousDayAttendance === 'number' && typeof data.totalPreviousDayAttendanceReg === 'number' && typeof data.totalPreviousDayContractualAttendance === 'number') {
          setCachedCounts('previousDay', data);
        }
      });
    }
  }, [countsData.previousDay, setCachedCounts]);



  return (
    <div className="min-h-screen w-full px-4 md:px-10 lg:px-20">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-[#6A1786]">
          {role === 'student' ? 'Student Attendance' : 'Staff Attendance'}
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/attendance/daily')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:shadow hover:border-purple-300 transition-all"
          >
            <CalendarDays className="w-4 h-4" />
            <span className="hidden sm:inline">Daily Register</span>
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/attendance/monthly')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:shadow hover:border-purple-300 transition-all"
          >
            <CalendarDays className="w-4 h-4" />
            <span className="hidden sm:inline">Monthly Register</span>
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/attendance/analytics')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:shadow hover:border-purple-300 transition-all"
          >
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">KV-wise List</span>
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/attendance/analytics/compare')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#6A1786] to-[#8B2FA8] text-white font-medium rounded-lg shadow-md hover:shadow-lg hover:from-[#7a1f96] hover:to-[#9b3fb8] transition-all"
          >
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">Trends & Comparison</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Analytics Banner */}
      <div
        onClick={() => navigate('/attendance/analytics/compare')}
        className="mb-6 p-4 bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 border border-purple-200 rounded-xl cursor-pointer hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <BarChart3 className="w-6 h-6 text-[#6A1786]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">📊 Attendance Analytics & Comparison Dashboard</h3>
              <p className="text-sm text-gray-600">
                Compare trends across Regions • KV Schools • Regular vs Contractual • Employee performance tracking
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-purple-400" />
        </div>
      </div>
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 mb-8">
        {statData.map((stat, idx) => (
          <button
            key={idx}
            onClick={() => navigate('/attendance/analytics')}
            className={`flex items-center gap-2 sm:gap-3 md:gap-4 rounded-lg shadow bg-white p-2 sm:p-3 md:p-4 border-t-4 ${stat.iconColor} text-left hover:shadow-lg transition-shadow min-h-[80px] sm:min-h-[92px]`}
            style={{ cursor: 'pointer', borderTopColor: stat.iconColor }}
            aria-label="Open KV-wise analytics"
          >
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 'clamp(40px, 10vw, 56px)',
                height: 'clamp(40px, 10vw, 56px)',
                background: stat.iconColor,
                borderRadius: 10
              }}
            >
              <img src={stat.icon} alt={stat.label} style={{ width: '60%', height: '60%' }} className="flex-shrink-0 object-contain" />
            </div>
            <div className="flex-1 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-4 min-w-0">
              <div className="flex flex-col min-w-0 flex-1">
                <div className="text-[10px] sm:text-xs font-semibold text-gray-500 mb-1 truncate">{stat.label}</div>
                <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-[#6A1786] mb-1 truncate">{stat.value}</div>
              </div>

              {/* Vertical divider for larger screens */}
              <div className="hidden md:block h-10 border-l border-gray-200" />

              {/* Breakdown on right - stacked and aligned right on md+; on mobile show under the value */}
              <div className="flex flex-col items-end ml-0 md:ml-2 flex-shrink-0">
                {stat.breakdown && Array.isArray(stat.breakdown) && (
                  stat.breakdown.map((item: BreakdownItem, i: number) => (
                    <div key={i} className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                      <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-600 font-medium">{item.label}</span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-[#57C3F2] font-semibold">{item.value}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <div className="font-semibold text-2xl mb-8">Regular Staff Attendance</div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="min-h-[474px]">
              <ComparisonChart type="regular" />
            </div>
          </div>
        </div>
        <div>
          <div className="font-semibold text-2xl mb-8">Contractual Staff Attendance</div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="min-h-[474px]">
              <ComparisonChart type="contractual" />
            </div>
          </div>
        </div>
      </div>
      {/* In-Time Stats and Trend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="font-semibold text-2xl mb-8">Real Time Attendance Activity</div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="min-h-[474px]">
              <RealTimeAttendanceChart />
            </div>
          </div>
        </div>
        <div>
          <div className="font-semibold text-2xl mb-8">Today's In-Time Statistics</div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="min-h-[474px]">
              <InTimePieChart />
            </div>
          </div>
        </div>
      </div>
      {/* Regular vs Contractual Section */}
      <div className="grid grid-cols-1 gap-6 mt-8">
        <div>
          <div className="font-semibold text-2xl mb-8">Regular vs Contractual Attendance</div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="min-h-[474px]">
              <RegularContractualComparisonChart />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
